// src/pages/SearchResultsPage.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from '../config/axiosConfig'; 
import { 
  FaUser, 
  FaGraduationCap, 
  FaImage, 
  FaPalette,
  FaSearch,
  FaArrowLeft,
  FaSpinner,
  FaTimes,
  FaClock,
  FaBriefcase,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaStar,
  FaRegStar,
  FaCheckCircle,
  FaUsers,
  FaBuilding,
  FaCalendarAlt
} from 'react-icons/fa';
import './SearchResultsPage.css';

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get('q') || '';
    const category = queryParams.get('category') || 'all';
    
    setSearchQuery(query);
    setActiveCategory(category);
    
    if (query) {
      performSearch(query, category);
    } else {
      setLoading(false);
    }
  }, [location]);

  const performSearch = async (query, category = 'all') => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/search', {
        params: { q: query, type: category }
      });
      
      setResults(response.data);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to load search results. Please try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const navigateToSection = (sectionId) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleNewSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryFilter = (category) => {
    setActiveCategory(category);
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}&category=${category}`);
    }
  };

  const openItemModal = (item, category) => {
    setSelectedItem({ ...item, category });
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeItemModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    document.body.style.overflow = 'auto';
  };

  const getTotalResults = () => {
    if (!results || !results.results) return 0;
    return Object.values(results.results).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  };

  const getCategoryCount = (category) => {
    if (!results || !results.results) return 0;
    return results.results[category]?.length || 0;
  };

  if (loading) {
    return (
      <div className="search-results-page loading">
        <div className="container">
          <div className="loading-container">
            <FaSpinner className="loading-spinner-icon" />
            <p>Searching for <span className="highlight">{searchQuery}</span>...</p>
            <div className="loading-progress">
              <div className="loading-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="search-results-page error">
        <div className="container">
          <div className="error-container">
            <div className="error-icon">😕</div>
            <h1>Search Error</h1>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/')} className="btn btn-primary">
                Return to Home
              </button>
              <button onClick={() => performSearch(searchQuery)} className="btn btn-outline">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!searchQuery) {
    return (
      <div className="search-results-page">
        <div className="container">
          <div className="search-empty-state">
            <div className="empty-state-icon">
              <FaSearch />
            </div>
            <h1>Search Urugo WOC</h1>
            <p>Find women, departments, activities, and gallery images</p>
            
            <form onSubmit={handleNewSearch} className="search-form-large">
              <div className="search-input-wrapper-large">
                <FaSearch className="search-input-icon" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="What are you looking for?"
                  className="search-input-large"
                  autoFocus
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    className="clear-search-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-large">
                <FaSearch /> Search
              </button>
            </form>
            
            <div className="search-suggestions">
              <h3>Popular Searches</h3>
              <div className="suggestion-tags">
                <button onClick={() => setSearchQuery('tailoring')}>Tailoring</button>
                <button onClick={() => setSearchQuery('cooking')}>Cooking</button>
                <button onClick={() => setSearchQuery('agriculture')}>Agriculture</button>
                <button onClick={() => setSearchQuery('sewing')}>Sewing</button>
                <button onClick={() => setSearchQuery('leather')}>Leatherwork</button>
                <button onClick={() => setSearchQuery('handicrafts')}>Handicrafts</button>
                <button onClick={() => setSearchQuery('coffee')}>Coffee Making</button>
                <button onClick={() => setSearchQuery('soap')}>Soap Making</button>
                <button onClick={() => setSearchQuery('weaving')}>Weaving</button>
                <button onClick={() => setSearchQuery('housekeeping')}>Housekeeping</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalResults = getTotalResults();

  return (
    <div className="search-results-page">
      <div className="container">
        {/* Search Header */}
        <div className="search-results-header">
          <div className="header-top">
            <button 
              onClick={() => navigate('/')}
              className="back-button"
            >
              <FaArrowLeft /> <span>Back to Home</span>
            </button>
          </div>
          
          <div className="search-box-container">
            <form onSubmit={handleNewSearch} className="search-box-form">
              <div className="search-input-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search again..."
                  className="search-box-input"
                />
                {searchQuery && (
                  <button 
                    type="button" 
                    className="clear-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              <button type="submit" className="search-box-button">
                <FaSearch />
              </button>
            </form>
          </div>
          
          <div className="search-info">
            <h1>Search Results</h1>
            <div className="search-meta">
              <span className="search-query">"{results?.query || searchQuery}"</span>
              <span className="search-count">{totalResults} results found</span>
            </div>
          </div>

          {/* Category Filters */}
          <div className="category-filters">
            <button 
              className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryFilter('all')}
            >
              All ({totalResults})
            </button>
            {results?.results?.women?.length > 0 && (
              <button 
                className={`filter-btn ${activeCategory === 'women' ? 'active' : ''}`}
                onClick={() => handleCategoryFilter('women')}
              >
                <FaUser /> Women ({getCategoryCount('women')})
              </button>
            )}
            {results?.results?.departments?.length > 0 && (
              <button 
                className={`filter-btn ${activeCategory === 'departments' ? 'active' : ''}`}
                onClick={() => handleCategoryFilter('departments')}
              >
                <FaGraduationCap /> Departments ({getCategoryCount('departments')})
              </button>
            )}
            {results?.results?.activities?.length > 0 && (
              <button 
                className={`filter-btn ${activeCategory === 'activities' ? 'active' : ''}`}
                onClick={() => handleCategoryFilter('activities')}
              >
                <FaImage /> Activities ({getCategoryCount('activities')})
              </button>
            )}
            {results?.results?.gallery?.length > 0 && (
              <button 
                className={`filter-btn ${activeCategory === 'gallery' ? 'active' : ''}`}
                onClick={() => handleCategoryFilter('gallery')}
              >
                <FaPalette /> Gallery ({getCategoryCount('gallery')})
              </button>
            )}
          </div>
        </div>

        {/* Results by Category */}
        <div className="search-results-grid">
          {/* Women Results */}
          {(activeCategory === 'all' || activeCategory === 'women') && results?.results?.women?.length > 0 && (
            <div className="search-category-section">
              <div className="category-header">
                <div className="category-header-left">
                  <FaUser className="category-icon" />
                  <h2>Trained Women</h2>
                </div>
                <span className="category-count">{results.results.women.length}</span>
              </div>
              
              <div className="category-results-grid women-grid">
                {results.results.women.map((woman, index) => (
                  <div 
                    key={index} 
                    className="result-card woman-card"
                    onClick={() => openItemModal(woman, 'women')}
                    style={{ '--animation-delay': `${index * 0.1}s` }}
                  >
                    <div className="result-image">
                      {woman.photo_url ? (
                        <img src={woman.photo_url} alt={woman.name} />
                      ) : (
                        <div className="result-avatar">
                          {woman.name?.charAt(0) || 'W'}
                        </div>
                      )}
                      <div className={`status-indicator ${woman.is_available ? 'available' : 'busy'}`}>
                        {woman.is_available ? 'Available' : 'Working'}
                      </div>
                    </div>
                    <div className="result-content">
                      <h3>{woman.name}</h3>
                      <p className="result-department">
                        <FaBuilding className="inline-icon" /> {woman.department_name || 'No Department'}
                      </p>
                      <p className="result-skills">{woman.skills || 'No skills listed'}</p>
                      <div className="result-meta">
                        {woman.experience_years > 0 && (
                          <span className="meta-item">
                            <FaClock /> {woman.experience_years} years exp.
                          </span>
                        )}
                        {woman.has_certificate && (
                          <span className="meta-item certificate">
                            <FaCheckCircle /> Certified
                          </span>
                        )}
                      </div>
                      <button className="btn btn-small btn-view">
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Departments Results */}
          {(activeCategory === 'all' || activeCategory === 'departments') && results?.results?.departments?.length > 0 && (
            <div className="search-category-section">
              <div className="category-header">
                <div className="category-header-left">
                  <FaGraduationCap className="category-icon" />
                  <h2>Training Departments</h2>
                </div>
                <span className="category-count">{results.results.departments.length}</span>
              </div>
              
              <div className="category-results-grid departments-grid">
                {results.results.departments.map((dept, index) => (
                  <div 
                    key={index} 
                    className="result-card department-card"
                    onClick={() => openItemModal(dept, 'departments')}
                    style={{ '--animation-delay': `${index * 0.1}s` }}
                  >
                    <div className="result-content">
                      <div className="department-icon-wrapper">
                        <FaGraduationCap className="dept-icon" />
                      </div>
                      <h3>{dept.name}</h3>
                      <p className="result-description">
                        {dept.description?.substring(0, 150) || 'No description available'}
                        {dept.description?.length > 150 && '...'}
                      </p>
                      <div className="dept-stats">
                        <span><FaUsers /> {dept.women_count || 0} Women</span>
                        <span><FaBriefcase /> {dept.request_count || 0} Requests</span>
                      </div>
                      <button className="btn btn-small btn-view">
                        View Department
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activities Results */}
          {(activeCategory === 'all' || activeCategory === 'activities') && results?.results?.activities?.length > 0 && (
            <div className="search-category-section">
              <div className="category-header">
                <div className="category-header-left">
                  <FaImage className="category-icon" />
                  <h2>Activities</h2>
                </div>
                <span className="category-count">{results.results.activities.length}</span>
              </div>
              
              <div className="category-results-grid activities-grid">
                {results.results.activities.map((activity, index) => (
                  <div 
                    key={index} 
                    className="result-card activity-card"
                    onClick={() => openItemModal(activity, 'activities')}
                    style={{ '--animation-delay': `${index * 0.1}s` }}
                  >
                    <div className="result-image">
                      {activity.image_url ? (
                        <img src={activity.image_url} alt={activity.description} />
                      ) : (
                        <div className="result-placeholder">
                          <FaImage />
                        </div>
                      )}
                    </div>
                    <div className="result-content">
                      <p className="result-description">{activity.description}</p>
                      <p className="result-department">
                        <FaBuilding className="inline-icon" /> {activity.department_name}
                      </p>
                      <button className="btn btn-small btn-view">
                        View Activity
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery Results */}
          {(activeCategory === 'all' || activeCategory === 'gallery') && results?.results?.gallery?.length > 0 && (
            <div className="search-category-section">
              <div className="category-header">
                <div className="category-header-left">
                  <FaPalette className="category-icon" />
                  <h2>Gallery Images</h2>
                </div>
                <span className="category-count">{results.results.gallery.length}</span>
              </div>
              
              <div className="category-results-grid gallery-grid">
                {results.results.gallery.map((image, index) => (
                  <div 
                    key={index} 
                    className="result-card gallery-card"
                    onClick={() => openItemModal(image, 'gallery')}
                    style={{ '--animation-delay': `${index * 0.1}s` }}
                  >
                    <div className="result-image">
                      {image.image_url ? (
                        <img src={image.image_url} alt={image.title} />
                      ) : (
                        <div className="result-placeholder">
                          <FaPalette />
                        </div>
                      )}
                    </div>
                    <div className="result-content">
                      <h3>{image.title || 'Untitled'}</h3>
                      <p className="result-description">
                        {image.description?.substring(0, 100) || 'No description'}
                        {image.description?.length > 100 && '...'}
                      </p>
                      <p className="result-department">
                        <FaBuilding className="inline-icon" /> {image.department_name || 'General'}
                      </p>
                      <button className="btn btn-small btn-view">
                        View Image
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* No Results */}
        {totalResults === 0 && (
          <div className="no-results-section">
            <div className="no-results-content">
              <div className="no-results-icon">🔍</div>
              <h2>No results found for "{results?.query || searchQuery}"</h2>
              <p>Try these suggestions:</p>
              <div className="suggestion-buttons">
                <button onClick={() => setSearchQuery('tailoring')}>Tailoring Department</button>
                <button onClick={() => setSearchQuery('women')}>Trained Women</button>
                <button onClick={() => setSearchQuery('activities')}>Activities</button>
                <button onClick={() => setSearchQuery('gallery')}>Gallery</button>
              </div>
              <button onClick={() => navigate('/')} className="btn btn-primary">
                Browse All Content
              </button>
            </div>
          </div>
        )}

        {/* Search Tips */}
        <div className="search-tips">
          <div className="tips-icon">💡</div>
          <div className="tips-content">
            <h3>Search Tips</h3>
            <ul>
              <li>Use specific keywords like "tailoring", "cooking", or "agriculture"</li>
              <li>Try searching for department names or skills</li>
              <li>Use fewer words for broader results</li>
              <li>Check your spelling if you're not getting results</li>
            </ul>
          </div>
        </div>

        {/* Item Detail Modal */}
        {showModal && selectedItem && (
          <div className="item-modal-overlay" onClick={closeItemModal}>
            <div className="item-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={closeItemModal}>
                <FaTimes />
              </button>
              
              {selectedItem.category === 'women' && (
                <div className="modal-woman-detail">
                  <div className="modal-image">
                    {selectedItem.photo_url ? (
                      <img src={selectedItem.photo_url} alt={selectedItem.name} />
                    ) : (
                      <div className="modal-avatar">
                        {selectedItem.name?.charAt(0) || 'W'}
                      </div>
                    )}
                  </div>
                  <div className="modal-info">
                    <h2>{selectedItem.name}</h2>
                    <p className="modal-department">
                      <FaBuilding /> {selectedItem.department_name || 'No Department'}
                    </p>
                    <div className="modal-badges">
                      <span className={`modal-badge ${selectedItem.is_available ? 'available' : 'busy'}`}>
                        {selectedItem.is_available ? 'Available for Work' : 'Currently Working'}
                      </span>
                      {selectedItem.has_certificate && (
                        <span className="modal-badge certified">
                          <FaCheckCircle /> Certified
                        </span>
                      )}
                    </div>
                    <div className="modal-details">
                      <div className="detail-item">
                        <FaClock /> <strong>Experience:</strong> {selectedItem.experience_years || 0} years
                      </div>
                      <div className="detail-item">
                        <FaPhone /> <strong>Phone:</strong> {selectedItem.phone || 'Not provided'}
                      </div>
                      <div className="detail-item">
                        <FaEnvelope /> <strong>Email:</strong> {selectedItem.email || 'Not provided'}
                      </div>
                      <div className="detail-item">
                        <FaMapMarkerAlt /> <strong>Location:</strong> {selectedItem.location || 'Not specified'}
                      </div>
                    </div>
                    <div className="modal-skills">
                      <h4>Skills</h4>
                      <p>{selectedItem.skills || 'No skills listed'}</p>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigateToSection('women')}
                    >
                      View in Women Section
                    </button>
                  </div>
                </div>
              )}

              {selectedItem.category === 'departments' && (
                <div className="modal-department-detail">
                  <div className="modal-info">
                    <h2>{selectedItem.name}</h2>
                    <p className="modal-description">{selectedItem.description}</p>
                    <div className="modal-stats">
                      <div className="stat-item">
                        <FaUsers /> <strong>{selectedItem.women_count || 0}</strong> Women
                      </div>
                      <div className="stat-item">
                        <FaBriefcase /> <strong>{selectedItem.request_count || 0}</strong> Requests
                      </div>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigateToSection('departments')}
                    >
                      View in Departments
                    </button>
                  </div>
                </div>
              )}

              {(selectedItem.category === 'activities' || selectedItem.category === 'gallery') && (
                <div className="modal-media-detail">
                  <div className="modal-image">
                    {selectedItem.image_url ? (
                      <img src={selectedItem.image_url} alt={selectedItem.title || selectedItem.description} />
                    ) : (
                      <div className="modal-placeholder">
                        <FaImage />
                      </div>
                    )}
                  </div>
                  <div className="modal-info">
                    <h2>{selectedItem.title || selectedItem.department_name || 'Activity'}</h2>
                    <p className="modal-description">{selectedItem.description}</p>
                    <p className="modal-department">
                      <FaBuilding /> {selectedItem.department_name || 'General'}
                    </p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        closeItemModal();
                        if (selectedItem.category === 'gallery') {
                          navigate('/gallery');
                        } else {
                          navigateToSection('departments');
                        }
                      }}
                    >
                      {selectedItem.category === 'gallery' ? 'View in Gallery' : 'View in Departments'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;